import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Battery, BatteryCharging, Users, Plus, LogIn, LogOut, Send,
  Heart, Brain, Dumbbell, Sparkles, Copy, Check, UserPlus, RefreshCw,
  Clock, Lightbulb,
  LayoutDashboard, ClipboardList, FileText, Download, TrendingUp, TrendingDown,
  Sun, Moon, Calendar, Target, ArrowRight,
  Zap, Shield, BarChart3, Activity, ChevronRight, ArrowLeft,
  Settings, Bell, BellRing, Timer, AlertTriangle, X,
  Sunrise, MoonStar, Trash2, UserX, UserCheck, ShieldCheck, Hash
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, AreaChart, Area
} from 'recharts';
import { auth, groups, checkins, support, preferences, alerts, admin, user } from './api';
import {
  requestNotificationPermission, subscribeToPush, unsubscribeFromPush,
  getNotificationPermission, isPushSupported, getExistingSubscription,
} from './notifications';
import './App.css';

const ENERGY_TYPES = [
  { key: 'emotional', label: 'Emotional', icon: Heart, color: '#f472b6' },
  { key: 'mental', label: 'Mental', icon: Brain, color: '#60a5fa' },
  { key: 'physical', label: 'Physical', icon: Dumbbell, color: '#34d399' },
  { key: 'spiritual', label: 'Spiritual', icon: Sparkles, color: '#c084fc' },
];

const RECHARGE_SUGGESTIONS = {
  emotional: [
    'Call your sponsor',
    'Talk to a trusted friend',
    'Journal your feelings',
    'Attend a meeting',
  ],
  mental: [
    'Try a meditation',
    'Read recovery literature',
    'Do some step work',
    'Take a quiet moment',
  ],
  physical: [
    'Go for a walk',
    'Eat something healthy',
    'Get some rest',
    'Do light exercise',
  ],
  spiritual: [
    'Spend time in prayer',
    'Practice meditation',
    'Write a gratitude list',
    'Do some service work',
  ],
};

const TAB_CONFIG = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'checkin', label: 'Check-In', icon: Battery },
  { key: 'groups', label: 'Groups', icon: Users },
  { key: 'reports', label: 'Reports', icon: FileText },
  { key: 'settings', label: 'Settings', icon: Settings },
  { key: 'admin', label: 'Admin', icon: ShieldCheck, adminOnly: true },
];

function getTimeAgo(timestamp) {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function formatTime(timestamp) {
  return new Date(timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function getAvg(entry) {
  const vals = ENERGY_TYPES.map((t) => entry[t.key] ?? 0);
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

function getDayName(dateStr) {
  return new Date(dateStr).toLocaleDateString([], { weekday: 'short' });
}

// ─── Shared Components ───

function RechargeGuide({ levels, compact }) {
  const lowTypes = ENERGY_TYPES.filter((t) => (levels[t.key] ?? 100) < 50);
  if (lowTypes.length === 0) return null;

  return (
    <div className={`recharge-guide ${compact ? 'recharge-guide-compact' : ''}`}>
      <div className="recharge-guide-header">
        <Lightbulb size={compact ? 14 : 16} />
        <span>Recharge Suggestions</span>
      </div>
      <div className="recharge-guide-sections">
        {lowTypes.map((type) => {
          const isCritical = (levels[type.key] ?? 100) < 25;
          return (
            <div key={type.key} className={`recharge-section ${isCritical ? 'recharge-critical' : ''}`}>
              <div className="recharge-section-label">
                <type.icon size={compact ? 12 : 14} color={type.color} />
                <span style={{ color: type.color }}>{type.label}</span>
                {isCritical && <span className="critical-badge">Critical</span>}
              </div>
              <div className="recharge-suggestions">
                {RECHARGE_SUGGESTIONS[type.key].map((s, i) => (
                  <span key={i} className="recharge-chip">{s}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BatteryGauge({ value, color, label, icon: Icon, onChange, readOnly }) {
  const level = Math.round(value);
  const fillColor = level <= 25 ? '#ef4444' : level <= 50 ? '#f59e0b' : color;

  return (
    <div className="battery-gauge">
      <div className="battery-header">
        <Icon size={18} color={color} />
        <span className="battery-label">{label}</span>
        <span className="battery-value" style={{ color: fillColor }}>{level}%</span>
      </div>
      <div className="battery-track">
        <div
          className="battery-fill"
          style={{ width: `${level}%`, backgroundColor: fillColor }}
        />
      </div>
      {!readOnly && (
        <input
          type="range"
          min="0"
          max="100"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="battery-slider"
          style={{ '--thumb-color': color }}
        />
      )}
    </div>
  );
}

// ─── Landing Page ───

const FEATURES = [
  {
    icon: Battery,
    title: 'Track 4 Energy Dimensions',
    desc: 'Monitor your emotional, mental, physical, and spiritual energy throughout the day with simple hourly check-ins.',
    color: '#6366f1',
  },
  {
    icon: Lightbulb,
    title: 'Personalized Recharge Suggestions',
    desc: 'Get tailored suggestions when your energy dips. Know exactly what activities help you recharge.',
    color: '#f59e0b',
  },
  {
    icon: Users,
    title: 'Group Support & Accountability',
    desc: 'Build a circle of support. Your group gets notified when you need encouragement most.',
    color: '#22c55e',
  },
  {
    icon: BarChart3,
    title: 'Daily & Weekly Insights',
    desc: 'See trends in your energy over time. Identify your peak hours and build a routine around them.',
    color: '#60a5fa',
  },
  {
    icon: Activity,
    title: 'See What Drains vs. Recharges',
    desc: 'Discover which activities lift you up and which ones drain you. Use data to shape healthier habits.',
    color: '#f472b6',
  },
  {
    icon: Shield,
    title: 'Built for Recovery',
    desc: 'Designed with recovery in mind. Track progress, share with your sponsor, and stay accountable.',
    color: '#c084fc',
  },
];

function LandingPage({ onGetStarted, onSignIn }) {
  return (
    <div className="landing">
      {/* Nav */}
      <nav className="landing-nav">
        <div className="landing-nav-brand">
          <BatteryCharging size={24} className="logo-icon" />
          <span className="landing-nav-title">Recovery Recharge</span>
        </div>
        <div className="landing-nav-actions">
          <button className="btn btn-small" onClick={onSignIn}>
            <LogIn size={16} /> Sign In
          </button>
          <button className="btn btn-primary" style={{ padding: '8px 18px', fontSize: '14px' }} onClick={onGetStarted}>
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <div className="hero-glow" />
        <div className="hero-content">
          <div className="hero-badge">
            <Zap size={14} />
            <span>Your recovery, powered by awareness</span>
          </div>
          <h1 className="hero-title">
            Know Your Energy.<br />
            <span className="hero-title-accent">Own Your Recovery.</span>
          </h1>
          <p className="hero-subtitle">
            Track your energy. Build healthy habits. Support your recovery community.
            Recovery Recharge helps you see what drains you, what recharges you,
            and how to build a life full of energy.
          </p>
          <div className="hero-actions">
            <button className="btn btn-primary btn-lg" onClick={onGetStarted}>
              <BatteryCharging size={20} />
              Get Started Free
              <ChevronRight size={18} />
            </button>
            <button className="btn btn-outline btn-lg" onClick={onSignIn}>
              Sign In
            </button>
          </div>
        </div>

        {/* Floating battery indicators */}
        <div className="hero-visual">
          <div className="hero-battery-grid">
            {ENERGY_TYPES.map((type, i) => (
              <div key={type.key} className="hero-battery" style={{ animationDelay: `${i * 0.15}s` }}>
                <type.icon size={22} color={type.color} />
                <span className="hero-battery-label">{type.label}</span>
                <div className="hero-battery-track">
                  <div
                    className="hero-battery-fill"
                    style={{
                      width: `${65 + i * 8}%`,
                      backgroundColor: type.color,
                    }}
                  />
                </div>
                <span className="hero-battery-val" style={{ color: type.color }}>{65 + i * 8}%</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="landing-features">
        <div className="features-header">
          <h2 className="features-title">Everything you need to stay recharged</h2>
          <p className="features-subtitle">
            Built specifically for people in recovery who want to understand their energy
            and build life-giving routines.
          </p>
        </div>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="feature-card">
              <div className="feature-icon" style={{ backgroundColor: `${f.color}15`, color: f.color }}>
                <f.icon size={24} />
              </div>
              <h3 className="feature-title">{f.title}</h3>
              <p className="feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="landing-how">
        <h2 className="how-title">How It Works</h2>
        <div className="how-steps">
          <div className="how-step">
            <div className="how-step-num">1</div>
            <h3>Check In</h3>
            <p>Rate your emotional, mental, physical, and spiritual energy throughout the day.</p>
          </div>
          <div className="how-step-arrow"><ArrowRight size={24} /></div>
          <div className="how-step">
            <div className="how-step-num">2</div>
            <h3>See Patterns</h3>
            <p>Discover what activities recharge you and what drains your energy.</p>
          </div>
          <div className="how-step-arrow"><ArrowRight size={24} /></div>
          <div className="how-step">
            <div className="how-step-num">3</div>
            <h3>Recharge</h3>
            <p>Get personalized suggestions and support from your recovery community.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="landing-cta">
        <div className="cta-glow" />
        <BatteryCharging size={40} className="cta-icon" />
        <h2 className="cta-title">Ready to take charge of your energy?</h2>
        <p className="cta-subtitle">
          Join others in recovery who are building healthier, more intentional lives.
        </p>
        <button className="btn btn-primary btn-lg" onClick={onGetStarted}>
          <Zap size={20} />
          Start Your Journey
        </button>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <BatteryCharging size={18} className="logo-icon" />
        <span>Recovery Recharge</span>
        <span className="footer-sep">&middot;</span>
        <span className="footer-muted">One check-in at a time.</span>
      </footer>
    </div>
  );
}

// ─── Auth ───

function AuthForm({ onLogin, initialSignup, onBack }) {
  const [isSignup, setIsSignup] = useState(initialSignup || false);
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = isSignup ? form : { email: form.email, password: form.password };
      const res = isSignup ? await auth.signup(payload) : await auth.login(payload);
      const { token, user } = res.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      onLogin(user, isSignup);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        {onBack && (
          <button className="btn btn-back" onClick={onBack}>
            <ArrowLeft size={16} /> Back
          </button>
        )}
        <div className="auth-logo">
          <BatteryCharging size={48} className="logo-icon" />
          <h1>Recovery Recharge</h1>
          <p className="auth-subtitle">Track your energy. Support your people.</p>
        </div>
        <form onSubmit={handleSubmit} className="auth-form">
          {isSignup && (
            <input
              type="text"
              placeholder="Username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
          />
          {error && <div className="error-msg">{error}</div>}
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <RefreshCw size={18} className="spin" /> : isSignup ? <UserPlus size={18} /> : <LogIn size={18} />}
            {isSignup ? 'Create Account' : 'Sign In'}
          </button>
        </form>
        <button className="btn btn-link" onClick={() => { setIsSignup(!isSignup); setError(''); }}>
          {isSignup ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
        </button>
      </div>
    </div>
  );
}

// ─── Onboarding Flow ───

const FIRST_CHECKIN_OPTIONS = [
  { value: 0, label: 'Right after waking' },
  { value: 30, label: '30 minutes after waking' },
  { value: 60, label: '1 hour after waking' },
  { value: 90, label: '1.5 hours after waking' },
  { value: 120, label: '2 hours after waking' },
];

function OnboardingFlow({ onComplete }) {
  const [step, setStep] = useState(0);
  const [prefs, setPrefs] = useState({
    wakeTime: '07:00',
    bedtime: '22:00',
    firstCheckinDelay: 0,
    checkinFrequencyHours: 2,
    bedtimeCheckinEnabled: true,
  });
  const [saving, setSaving] = useState(false);

  const totalSteps = 6;

  const handleFinish = async () => {
    setSaving(true);
    try {
      await preferences.update({
        ...prefs,
        onboardingCompleted: true,
      });
      onComplete(prefs);
    } catch (err) {
      console.error('Failed to save onboarding preferences:', err);
      onComplete(prefs);
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    if (step < totalSteps - 1) setStep(step + 1);
    else handleFinish();
  };

  const back = () => {
    if (step > 0) setStep(step - 1);
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-card">
        <div className="onboarding-progress">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`onboarding-dot ${i === step ? 'active' : i < step ? 'done' : ''}`}
            />
          ))}
        </div>

        {step === 0 && (
          <div className="onboarding-step">
            <div className="onboarding-icon-wrap">
              <BatteryCharging size={48} className="logo-icon" />
            </div>
            <h2 className="onboarding-title">Welcome{user.name ? `, ${user.name}` : ''}!</h2>
            <p className="onboarding-text">
              Recovery Recharge helps you track your energy across four dimensions
              — emotional, mental, physical, and spiritual — so you can build
              healthier routines and stay connected to your recovery community.
            </p>
            <p className="onboarding-text onboarding-text-muted">
              Let's personalize your experience so check-in reminders fit naturally
              into your day.
            </p>
          </div>
        )}

        {step === 1 && (
          <div className="onboarding-step">
            <div className="onboarding-icon-wrap onboarding-icon-warm">
              <Sunrise size={40} />
            </div>
            <h2 className="onboarding-title">What time do you typically wake up?</h2>
            <p className="onboarding-text">We won't send any notifications before this time.</p>
            <input
              type="time"
              className="onboarding-time-input"
              value={prefs.wakeTime}
              onChange={(e) => setPrefs({ ...prefs, wakeTime: e.target.value })}
            />
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-step">
            <div className="onboarding-icon-wrap onboarding-icon-cool">
              <MoonStar size={40} />
            </div>
            <h2 className="onboarding-title">What time is your bedtime?</h2>
            <p className="onboarding-text">We'll respect your wind-down time — no late-night pings.</p>
            <input
              type="time"
              className="onboarding-time-input"
              value={prefs.bedtime}
              onChange={(e) => setPrefs({ ...prefs, bedtime: e.target.value })}
            />
          </div>
        )}

        {step === 3 && (
          <div className="onboarding-step">
            <div className="onboarding-icon-wrap">
              <Clock size={40} />
            </div>
            <h2 className="onboarding-title">When do you want your first check-in?</h2>
            <p className="onboarding-text">Choose when to start tracking after you wake up.</p>
            <div className="onboarding-options">
              {FIRST_CHECKIN_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`onboarding-option ${prefs.firstCheckinDelay === opt.value ? 'selected' : ''}`}
                  onClick={() => setPrefs({ ...prefs, firstCheckinDelay: opt.value })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="onboarding-step">
            <div className="onboarding-icon-wrap">
              <Timer size={40} />
            </div>
            <h2 className="onboarding-title">How often during the day?</h2>
            <p className="onboarding-text">We'll space reminders evenly across your waking hours.</p>
            <div className="onboarding-options">
              {FREQUENCY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`onboarding-option ${prefs.checkinFrequencyHours === opt.value ? 'selected' : ''}`}
                  onClick={() => setPrefs({ ...prefs, checkinFrequencyHours: opt.value })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="onboarding-step">
            <div className="onboarding-icon-wrap onboarding-icon-cool">
              <Moon size={40} />
            </div>
            <h2 className="onboarding-title">Bedtime check-in reminder?</h2>
            <p className="onboarding-text">
              A gentle reminder 30 minutes before bedtime to reflect on your day
              and check in one last time.
            </p>
            <div className="onboarding-toggle-row">
              <button
                className={`onboarding-toggle-btn ${prefs.bedtimeCheckinEnabled ? 'selected' : ''}`}
                onClick={() => setPrefs({ ...prefs, bedtimeCheckinEnabled: true })}
              >
                <Check size={18} />
                Yes, remind me
              </button>
              <button
                className={`onboarding-toggle-btn ${!prefs.bedtimeCheckinEnabled ? 'selected' : ''}`}
                onClick={() => setPrefs({ ...prefs, bedtimeCheckinEnabled: false })}
              >
                <X size={18} />
                No thanks
              </button>
            </div>
          </div>
        )}

        <div className="onboarding-actions">
          {step > 0 && (
            <button className="btn btn-small" onClick={back}>
              <ArrowLeft size={16} /> Back
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={next}
            disabled={saving}
            style={{ marginLeft: 'auto' }}
          >
            {saving ? (
              <><RefreshCw size={16} className="spin" /> Saving...</>
            ) : step === totalSteps - 1 ? (
              <><Sparkles size={16} /> Finish Setup</>
            ) : (
              <>Next <ChevronRight size={16} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Smart Schedule Helpers ───

function parseTime(timeStr) {
  const [h, m] = (timeStr || '07:00').split(':').map(Number);
  return h * 60 + m;
}

function formatMinutes(totalMins) {
  const h = Math.floor(totalMins / 60) % 24;
  const m = totalMins % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function calculateScheduleTimes(prefs) {
  const wakeMins = parseTime(prefs.wakeTime);
  const bedMins = parseTime(prefs.bedtime);
  const freq = prefs.checkinFrequencyHours || 2;
  const delay = prefs.firstCheckinDelay || 0;
  const freqMins = freq * 60;

  const effectiveBed = bedMins <= wakeMins ? bedMins + 1440 : bedMins;
  const firstCheckin = wakeMins + delay;

  const times = [];
  let current = firstCheckin;
  while (current < effectiveBed - 30) {
    times.push({ minutes: current % 1440, label: formatMinutes(current), type: 'checkin' });
    current += freqMins;
  }

  if (prefs.bedtimeCheckinEnabled) {
    const bedtimeReminder = effectiveBed - 30;
    times.push({ minutes: bedtimeReminder % 1440, label: formatMinutes(bedtimeReminder), type: 'bedtime' });
  }

  return times;
}

function ScheduleTimeline({ prefs }) {
  const times = useMemo(() => calculateScheduleTimes(prefs), [prefs]);
  const wakeMins = parseTime(prefs.wakeTime);
  const bedMins = parseTime(prefs.bedtime);
  const effectiveBed = bedMins <= wakeMins ? bedMins + 1440 : bedMins;
  const totalWaking = effectiveBed - wakeMins;

  return (
    <div className="schedule-timeline">
      <div className="schedule-timeline-bar">
        <div className="schedule-wake-label">
          <Sunrise size={14} />
          <span>{formatMinutes(wakeMins)}</span>
        </div>
        <div className="schedule-bar-track">
          {times.map((t, i) => {
            const pos = ((t.type === 'bedtime' ? (effectiveBed - 30) : (t.minutes >= wakeMins ? t.minutes : t.minutes + 1440)) - wakeMins) / totalWaking * 100;
            return (
              <div
                key={i}
                className={`schedule-bar-marker ${t.type === 'bedtime' ? 'marker-bedtime' : ''}`}
                style={{ left: `${Math.min(pos, 100)}%` }}
                title={`${t.label}${t.type === 'bedtime' ? ' (bedtime)' : ''}`}
              />
            );
          })}
        </div>
        <div className="schedule-bed-label">
          <MoonStar size={14} />
          <span>{formatMinutes(bedMins)}</span>
        </div>
      </div>
      <div className="schedule-times-list">
        {times.map((t, i) => (
          <span key={i} className={`schedule-time-chip ${t.type === 'bedtime' ? 'chip-bedtime' : ''}`}>
            {t.type === 'bedtime' ? <Moon size={12} /> : <Bell size={12} />}
            {t.label}
          </span>
        ))}
      </div>
      <div className="schedule-summary">
        {times.length} check-in{times.length !== 1 ? 's' : ''} per day
        {prefs.bedtimeCheckinEnabled ? ' (including bedtime reminder)' : ''}
      </div>
    </div>
  );
}

// ─── Check-In Tab Components ───

function CheckInPanel({ onCheckinComplete }) {
  const [levels, setLevels] = useState({ emotional: 50, mental: 50, physical: 50, spiritual: 50 });
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await checkins.create({ ...levels, ...(note.trim() && { note: note.trim() }) });
      setSaved(true);
      setNote('');
      if (onCheckinComplete) onCheckinComplete();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Check-in failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <Battery size={22} />
        <h2>Energy Check-In</h2>
      </div>
      <div className="gauges-grid">
        {ENERGY_TYPES.map((type) => (
          <BatteryGauge
            key={type.key}
            value={levels[type.key]}
            color={type.color}
            label={type.label}
            icon={type.icon}
            onChange={(val) => setLevels({ ...levels, [type.key]: val })}
          />
        ))}
      </div>
      <div className="checkin-note">
        <label className="checkin-note-label" htmlFor="checkin-note">
          What happened since your last check-in that affected your energy?
        </label>
        <textarea
          id="checkin-note"
          className="checkin-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional — jot down anything on your mind..."
          rows={3}
        />
      </div>
      <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>
        {saving ? <RefreshCw size={18} className="spin" /> : saved ? <Check size={18} /> : <BatteryCharging size={18} />}
        {saving ? 'Saving...' : saved ? 'Checked In!' : 'Submit Check-In'}
      </button>
    </div>
  );
}


function CheckInTimeline({ entries }) {
  const sortedEntries = useMemo(() => {
    if (!entries) return [];
    return [...entries].sort((a, b) => {
      const timeA = new Date(b.createdAt || b.timestamp).getTime();
      const timeB = new Date(a.createdAt || a.timestamp).getTime();
      return timeA - timeB;
    });
  }, [entries]);

  if (!entries) return null;

  return (
    <div className="card">
      <div className="card-header">
        <Clock size={22} />
        <h2>Today's Check-Ins</h2>
      </div>
      {sortedEntries.length === 0 ? (
        <p className="empty-state">No check-ins yet today</p>
      ) : (
        <div className="timeline">
          {sortedEntries.map((entry, i) => {
            const avg = getAvg(entry);
            const isLow = avg <= 35;
            return (
              <div key={entry._id || i} className={`timeline-entry ${isLow ? 'timeline-low' : ''}`}>
                <div className="timeline-dot" />
                <div className="timeline-content">
                  <div className="timeline-header">
                    <span className="timeline-time">{formatTime(entry.createdAt || entry.timestamp)}</span>
                    <span className={`avg-badge ${isLow ? 'badge-low' : avg <= 50 ? 'badge-mid' : 'badge-high'}`}>
                      {avg}% avg
                    </span>
                  </div>
                  <div className="timeline-gauges">
                    {ENERGY_TYPES.map((type) => (
                      <div key={type.key} className="timeline-gauge-row">
                        <type.icon size={14} color={type.color} />
                        <div className="timeline-gauge-track">
                          <div
                            className="timeline-gauge-fill"
                            style={{
                              width: `${entry[type.key] ?? 0}%`,
                              backgroundColor: (entry[type.key] ?? 0) <= 25 ? '#ef4444' : (entry[type.key] ?? 0) <= 50 ? '#f59e0b' : type.color
                            }}
                          />
                        </div>
                        <span className="timeline-gauge-val">{entry[type.key] ?? 0}%</span>
                      </div>
                    ))}
                  </div>
                  {entry.note && <p className="timeline-note">{entry.note}</p>}
                  <RechargeGuide levels={entry} compact />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Dashboard Tab ───

function EnergyTrendChart({ entries }) {
  const chartData = useMemo(() => {
    return entries.map((entry) => ({
      time: formatTime(entry.createdAt || entry.timestamp),
      Emotional: entry.emotional ?? 0,
      Mental: entry.mental ?? 0,
      Physical: entry.physical ?? 0,
      Spiritual: entry.spiritual ?? 0,
    }));
  }, [entries]);

  if (chartData.length < 2) {
    return (
      <div className="card">
        <div className="card-header">
          <TrendingUp size={22} />
          <h2>Energy Trends Today</h2>
        </div>
        <p className="empty-state">Need at least 2 check-ins to show trends</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <TrendingUp size={22} />
        <h2>Energy Trends Today</h2>
      </div>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2e3142" />
            <XAxis dataKey="time" stroke="#8b8fa3" fontSize={12} />
            <YAxis domain={[0, 100]} stroke="#8b8fa3" fontSize={12} />
            <Tooltip
              contentStyle={{
                background: '#1a1d27',
                border: '1px solid #2e3142',
                borderRadius: '8px',
                color: '#e4e4e7',
              }}
            />
            <Legend />
            <Line type="monotone" dataKey="Emotional" stroke="#f472b6" strokeWidth={2} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="Mental" stroke="#60a5fa" strokeWidth={2} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="Physical" stroke="#34d399" strokeWidth={2} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="Spiritual" stroke="#c084fc" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CurrentAverageCard({ entries }) {
  const currentAvg = useMemo(() => {
    if (entries.length === 0) return null;
    const latest = entries[entries.length - 1];
    const avgs = {};
    ENERGY_TYPES.forEach((t) => {
      avgs[t.key] = latest[t.key] ?? 0;
    });
    avgs.overall = getAvg(latest);
    return avgs;
  }, [entries]);

  const dayAvg = useMemo(() => {
    if (entries.length === 0) return null;
    const sums = { emotional: 0, mental: 0, physical: 0, spiritual: 0 };
    entries.forEach((e) => {
      ENERGY_TYPES.forEach((t) => { sums[t.key] += e[t.key] ?? 0; });
    });
    const avg = {};
    ENERGY_TYPES.forEach((t) => { avg[t.key] = Math.round(sums[t.key] / entries.length); });
    avg.overall = Math.round(ENERGY_TYPES.reduce((s, t) => s + avg[t.key], 0) / 4);
    return avg;
  }, [entries]);

  if (!currentAvg) {
    return (
      <div className="card">
        <div className="card-header">
          <BatteryCharging size={22} />
          <h2>Current Energy</h2>
        </div>
        <p className="empty-state">Complete a check-in to see your energy levels</p>
      </div>
    );
  }

  const overallColor = currentAvg.overall <= 25 ? '#ef4444' : currentAvg.overall <= 50 ? '#f59e0b' : '#22c55e';

  return (
    <div className="card">
      <div className="card-header">
        <BatteryCharging size={22} />
        <h2>Current Energy</h2>
      </div>
      <div className="energy-overview">
        <div className="energy-big-number" style={{ color: overallColor }}>
          {currentAvg.overall}%
        </div>
        <div className="energy-big-label">Overall Average</div>
        {dayAvg && entries.length > 1 && (
          <div className="energy-day-avg">Day average: {dayAvg.overall}% across {entries.length} check-ins</div>
        )}
      </div>
      <div className="gauges-grid" style={{ marginTop: 16 }}>
        {ENERGY_TYPES.map((type) => (
          <BatteryGauge
            key={type.key}
            value={currentAvg[type.key]}
            color={type.color}
            label={type.label}
            icon={type.icon}
            readOnly
          />
        ))}
      </div>
    </div>
  );
}

function DashboardTab({ entries, loading }) {
  if (loading) {
    return <p className="empty-state"><RefreshCw size={16} className="spin" /> Loading dashboard...</p>;
  }

  return (
    <div className="dashboard-layout">
      <div className="dashboard-top">
        <CurrentAverageCard entries={entries} />
      </div>
      <EnergyTrendChart entries={entries} />
      <CheckInTimeline entries={entries} />
    </div>
  );
}

// ─── Reports Tab ───

function analyzeCheckins(entries) {
  if (entries.length < 2) return null;

  const recharging = [];
  const draining = [];
  let peakEntry = entries[0];
  let lowestEntry = entries[0];
  let peakAvg = getAvg(entries[0]);
  let lowestAvg = getAvg(entries[0]);

  for (let i = 1; i < entries.length; i++) {
    const prev = entries[i - 1];
    const curr = entries[i];
    const prevAvg = getAvg(prev);
    const currAvg = getAvg(curr);
    const diff = currAvg - prevAvg;
    const time = formatTime(curr.createdAt || curr.timestamp);
    const note = curr.note || 'No note recorded';

    if (diff > 0) {
      recharging.push({ time, diff, note, entry: curr });
    } else if (diff < 0) {
      draining.push({ time, diff, note, entry: curr });
    }

    if (currAvg > peakAvg) {
      peakAvg = currAvg;
      peakEntry = curr;
    }
    if (currAvg < lowestAvg) {
      lowestAvg = currAvg;
      lowestEntry = curr;
    }
  }

  recharging.sort((a, b) => b.diff - a.diff);
  draining.sort((a, b) => a.diff - b.diff);

  return { recharging, draining, peakEntry, lowestEntry, peakAvg, lowestAvg };
}

function DailyReport({ entries }) {
  const analysis = useMemo(() => analyzeCheckins(entries), [entries]);

  const generatePDF = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const date = new Date().toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    let content = `RECOVERY RECHARGE - DAILY ENERGY REPORT\n`;
    content += `${'='.repeat(50)}\n`;
    content += `User: ${user.username || 'Unknown'}\n`;
    content += `Date: ${date}\n`;
    content += `Total Check-ins: ${entries.length}\n\n`;

    if (analysis) {
      content += `PEAK ENERGY: ${analysis.peakAvg}% at ${formatTime(analysis.peakEntry.createdAt || analysis.peakEntry.timestamp)}\n`;
      content += `LOWEST ENERGY: ${analysis.lowestAvg}% at ${formatTime(analysis.lowestEntry.createdAt || analysis.lowestEntry.timestamp)}\n\n`;

      if (analysis.recharging.length > 0) {
        content += `ACTIVITIES THAT RECHARGED YOU:\n`;
        content += `${'-'.repeat(30)}\n`;
        analysis.recharging.forEach((r) => {
          content += `  +${r.diff}% at ${r.time}: ${r.note}\n`;
        });
        content += `\n`;
      }

      if (analysis.draining.length > 0) {
        content += `ACTIVITIES THAT DRAINED YOU:\n`;
        content += `${'-'.repeat(30)}\n`;
        analysis.draining.forEach((d) => {
          content += `  ${d.diff}% at ${d.time}: ${d.note}\n`;
        });
        content += `\n`;
      }

      if (analysis.recharging.length > 0) {
        content += `RECOMMENDATION:\n`;
        content += `You recharged most when: "${analysis.recharging[0].note}"\n`;
        content += `Do more of this!\n\n`;
      }
    }

    content += `\nALL CHECK-INS:\n`;
    content += `${'-'.repeat(30)}\n`;
    entries.forEach((e) => {
      const time = formatTime(e.createdAt || e.timestamp);
      content += `${time} - Emotional: ${e.emotional}%, Mental: ${e.mental}%, Physical: ${e.physical}%, Spiritual: ${e.spiritual}% (Avg: ${getAvg(e)}%)\n`;
      if (e.note) content += `  Note: ${e.note}\n`;
    });

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `energy-report-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (entries.length < 2) {
    return (
      <div className="card">
        <div className="card-header">
          <ClipboardList size={22} />
          <h2>Daily Report</h2>
        </div>
        <p className="empty-state">Need at least 2 check-ins to generate a daily report</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <ClipboardList size={22} />
        <h2>Daily Report</h2>
        <button className="btn btn-small btn-accent" onClick={generatePDF}>
          <Download size={14} /> Download Report
        </button>
      </div>

      <div className="report-grid">
        <div className="report-stat-card report-peak">
          <Sun size={20} />
          <div className="report-stat-value">{analysis.peakAvg}%</div>
          <div className="report-stat-label">Peak Energy</div>
          <div className="report-stat-time">{formatTime(analysis.peakEntry.createdAt || analysis.peakEntry.timestamp)}</div>
        </div>
        <div className="report-stat-card report-low">
          <Moon size={20} />
          <div className="report-stat-value">{analysis.lowestAvg}%</div>
          <div className="report-stat-label">Lowest Energy</div>
          <div className="report-stat-time">{formatTime(analysis.lowestEntry.createdAt || analysis.lowestEntry.timestamp)}</div>
        </div>
      </div>

      {analysis.recharging.length > 0 && (
        <div className="report-section">
          <h3 className="report-section-title">
            <TrendingUp size={16} style={{ color: '#22c55e' }} />
            Activities That Recharged You
          </h3>
          <div className="report-activities">
            {analysis.recharging.map((r, i) => (
              <div key={i} className="report-activity recharge">
                <span className="activity-diff">+{r.diff}%</span>
                <span className="activity-time">{r.time}</span>
                <span className="activity-note">{r.note}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {analysis.draining.length > 0 && (
        <div className="report-section">
          <h3 className="report-section-title">
            <TrendingDown size={16} style={{ color: '#ef4444' }} />
            Activities That Drained You
          </h3>
          <div className="report-activities">
            {analysis.draining.map((d, i) => (
              <div key={i} className="report-activity drain">
                <span className="activity-diff">{d.diff}%</span>
                <span className="activity-time">{d.time}</span>
                <span className="activity-note">{d.note}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {analysis.recharging.length > 0 && (
        <div className="report-recommendation">
          <Lightbulb size={18} />
          <div>
            <strong>You recharged most when:</strong> "{analysis.recharging[0].note}"
            <br />
            <span className="report-rec-sub">Do more of this!</span>
          </div>
        </div>
      )}
    </div>
  );
}

function WeeklyReport({ weekEntries }) {
  const analysis = useMemo(() => {
    if (!weekEntries || weekEntries.length === 0) return null;

    // Group by day
    const byDay = {};
    weekEntries.forEach((e) => {
      const date = new Date(e.createdAt || e.timestamp).toLocaleDateString();
      if (!byDay[date]) byDay[date] = [];
      byDay[date].push(e);
    });

    // Daily averages for chart
    const dailyData = Object.entries(byDay).map(([date, dayEntries]) => {
      const avgs = {};
      ENERGY_TYPES.forEach((t) => {
        avgs[t.label] = Math.round(dayEntries.reduce((s, e) => s + (e[t.key] ?? 0), 0) / dayEntries.length);
      });
      return { day: getDayName(date), date, ...avgs, count: dayEntries.length };
    });

    // Overall analysis across week
    const allRecharging = [];
    const allDraining = [];
    const dayOfWeekAvgs = {};

    Object.entries(byDay).forEach(([date, dayEntries]) => {
      const dayName = getDayName(date);
      const dayAvg = Math.round(dayEntries.reduce((s, e) => s + getAvg(e), 0) / dayEntries.length);
      dayOfWeekAvgs[dayName] = dayAvg;

      for (let i = 1; i < dayEntries.length; i++) {
        const prev = dayEntries[i - 1];
        const curr = dayEntries[i];
        const diff = getAvg(curr) - getAvg(prev);
        const note = curr.note || 'No note recorded';
        if (diff > 0) allRecharging.push({ diff, note, day: dayName });
        else if (diff < 0) allDraining.push({ diff, note, day: dayName });
      }
    });

    allRecharging.sort((a, b) => b.diff - a.diff);
    allDraining.sort((a, b) => a.diff - b.diff);

    // Count life-giving activities (recharges)
    const totalCheckins = weekEntries.length;
    const rechargeRatio = totalCheckins > 1 ? allRecharging.length / (totalCheckins - Object.keys(byDay).length) : 0;

    return {
      dailyData,
      allRecharging: allRecharging.slice(0, 5),
      allDraining: allDraining.slice(0, 5),
      dayOfWeekAvgs,
      totalCheckins,
      totalDays: Object.keys(byDay).length,
      rechargeRatio,
    };
  }, [weekEntries]);

  const downloadWeeklyReport = () => {
    if (!analysis) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    let content = `RECOVERY RECHARGE - WEEKLY ENERGY REPORT\n`;
    content += `${'='.repeat(50)}\n`;
    content += `User: ${user.username || 'Unknown'}\n`;
    content += `Week ending: ${new Date().toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}\n`;
    content += `Total Check-ins: ${analysis.totalCheckins} across ${analysis.totalDays} days\n\n`;

    content += `DAILY AVERAGES:\n${'-'.repeat(30)}\n`;
    analysis.dailyData.forEach((d) => {
      content += `  ${d.day}: Emotional ${d.Emotional}%, Mental ${d.Mental}%, Physical ${d.Physical}%, Spiritual ${d.Spiritual}% (${d.count} check-ins)\n`;
    });

    if (analysis.allRecharging.length > 0) {
      content += `\nMOST RECHARGING ACTIVITIES:\n${'-'.repeat(30)}\n`;
      analysis.allRecharging.forEach((r) => { content += `  +${r.diff}% (${r.day}): ${r.note}\n`; });
    }
    if (analysis.allDraining.length > 0) {
      content += `\nMOST DRAINING ACTIVITIES:\n${'-'.repeat(30)}\n`;
      analysis.allDraining.forEach((d) => { content += `  ${d.diff}% (${d.day}): ${d.note}\n`; });
    }

    content += `\nGOAL TRACKING:\n${'-'.repeat(30)}\n`;
    content += `Recharge ratio: ${Math.round(analysis.rechargeRatio * 100)}% of transitions were positive\n`;
    content += analysis.rechargeRatio >= 0.5
      ? `Great job! You're doing enough life-giving activities.\n`
      : `Consider adding more life-giving activities to your routine.\n`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `weekly-report-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!analysis) {
    return (
      <div className="card">
        <div className="card-header">
          <Calendar size={22} />
          <h2>Weekly Summary</h2>
        </div>
        <p className="empty-state">No check-in data available for the past week</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <Calendar size={22} />
        <h2>Weekly Summary</h2>
        <button className="btn btn-small btn-accent" onClick={downloadWeeklyReport}>
          <Download size={14} /> Download Report
        </button>
      </div>

      <div className="report-meta">
        {analysis.totalCheckins} check-ins across {analysis.totalDays} days
      </div>

      {analysis.dailyData.length > 1 && (
        <div className="chart-container" style={{ marginBottom: 20 }}>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={analysis.dailyData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2e3142" />
              <XAxis dataKey="day" stroke="#8b8fa3" fontSize={12} />
              <YAxis domain={[0, 100]} stroke="#8b8fa3" fontSize={12} />
              <Tooltip
                contentStyle={{
                  background: '#1a1d27',
                  border: '1px solid #2e3142',
                  borderRadius: '8px',
                  color: '#e4e4e7',
                }}
              />
              <Legend />
              <Area type="monotone" dataKey="Emotional" stroke="#f472b6" fill="#f472b6" fillOpacity={0.1} strokeWidth={2} />
              <Area type="monotone" dataKey="Mental" stroke="#60a5fa" fill="#60a5fa" fillOpacity={0.1} strokeWidth={2} />
              <Area type="monotone" dataKey="Physical" stroke="#34d399" fill="#34d399" fillOpacity={0.1} strokeWidth={2} />
              <Area type="monotone" dataKey="Spiritual" stroke="#c084fc" fill="#c084fc" fillOpacity={0.1} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Average by day of week */}
      <div className="report-section">
        <h3 className="report-section-title">
          <Calendar size={16} /> Average Energy by Day
        </h3>
        <div className="day-avg-bars">
          {Object.entries(analysis.dayOfWeekAvgs).map(([day, avg]) => (
            <div key={day} className="day-avg-row">
              <span className="day-avg-label">{day}</span>
              <div className="day-avg-track">
                <div
                  className="day-avg-fill"
                  style={{
                    width: `${avg}%`,
                    backgroundColor: avg <= 25 ? '#ef4444' : avg <= 50 ? '#f59e0b' : '#22c55e',
                  }}
                />
              </div>
              <span className="day-avg-value">{avg}%</span>
            </div>
          ))}
        </div>
      </div>

      {analysis.allRecharging.length > 0 && (
        <div className="report-section">
          <h3 className="report-section-title">
            <TrendingUp size={16} style={{ color: '#22c55e' }} />
            Most Recharging Activities This Week
          </h3>
          <div className="report-activities">
            {analysis.allRecharging.map((r, i) => (
              <div key={i} className="report-activity recharge">
                <span className="activity-diff">+{r.diff}%</span>
                <span className="activity-time">{r.day}</span>
                <span className="activity-note">{r.note}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {analysis.allDraining.length > 0 && (
        <div className="report-section">
          <h3 className="report-section-title">
            <TrendingDown size={16} style={{ color: '#ef4444' }} />
            Most Draining Activities This Week
          </h3>
          <div className="report-activities">
            {analysis.allDraining.map((d, i) => (
              <div key={i} className="report-activity drain">
                <span className="activity-diff">{d.diff}%</span>
                <span className="activity-time">{d.day}</span>
                <span className="activity-note">{d.note}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Goal tracking */}
      <div className={`report-goal ${analysis.rechargeRatio >= 0.5 ? 'goal-good' : 'goal-needs-work'}`}>
        <Target size={18} />
        <div>
          <strong>Goal Tracking: Are you doing enough life-giving activities?</strong>
          <br />
          <span className="goal-detail">
            {Math.round(analysis.rechargeRatio * 100)}% of your energy transitions were positive.
            {analysis.rechargeRatio >= 0.5
              ? " Great job! Keep it up!"
              : " Consider adding more recharging activities to your routine."}
          </span>
        </div>
      </div>
    </div>
  );
}

function ReportsTab({ todayEntries, weekEntries, loadingWeek }) {
  return (
    <div className="reports-layout">
      <DailyReport entries={todayEntries} />
      {loadingWeek ? (
        <div className="card">
          <p className="empty-state"><RefreshCw size={16} className="spin" /> Loading weekly data...</p>
        </div>
      ) : (
        <WeeklyReport weekEntries={weekEntries} />
      )}
    </div>
  );
}

// ─── Groups Tab Components ───

function GroupPanel({ currentUser, onGroupChange }) {
  const [myGroups, setMyGroups] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState('');

  const loadGroups = useCallback(async () => {
    try {
      const res = await groups.getMy();
      setMyGroups(res.data);
    } catch (err) {
      console.error('Failed to load groups:', err);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await groups.create({ name: groupName });
      setGroupName('');
      setShowCreate(false);
      loadGroups();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create group');
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await groups.join(joinCode.toUpperCase());
      setJoinCode('');
      setShowJoin(false);
      loadGroups();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to join group');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedId(code);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="card">
      <div className="card-header">
        <Users size={22} />
        <h2>My Groups</h2>
        <div className="card-actions">
          <button className="btn btn-small" onClick={() => { setShowJoin(!showJoin); setShowCreate(false); setError(''); }}>
            <LogIn size={16} /> Join
          </button>
          <button className="btn btn-small btn-accent" onClick={() => { setShowCreate(!showCreate); setShowJoin(false); setError(''); }}>
            <Plus size={16} /> Create
          </button>
        </div>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="inline-form">
          <input
            type="text"
            placeholder="Group name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            required
          />
          <button type="submit" className="btn btn-primary">Create</button>
        </form>
      )}

      {showJoin && (
        <form onSubmit={handleJoin} className="inline-form">
          <input
            type="text"
            placeholder="6-character code"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 6))}
            maxLength={6}
            required
            className="code-input"
          />
          <button type="submit" className="btn btn-primary" disabled={joinCode.length !== 6}>Join</button>
        </form>
      )}

      {error && <div className="error-msg">{error}</div>}

      <div className="group-list">
        {myGroups.length === 0 ? (
          <p className="empty-state">No groups yet. Create or join one to get started!</p>
        ) : (
          myGroups.map((g) => (
            <div
              key={g._id || g.id}
              className="group-item"
              onClick={() => onGroupChange(g)}
            >
              <div className="group-info">
                <span className="group-name">{g.name}</span>
                <span className="group-meta">{g.members?.length || 0} members</span>
              </div>
              <button
                className="btn btn-icon"
                onClick={(e) => { e.stopPropagation(); copyCode(g.code); }}
                title="Copy invite code"
              >
                {copiedId === g.code ? <Check size={16} /> : <Copy size={16} />}
                <span className="code-label">{g.code}</span>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function MembersPanel({ group }) {
  const [members, setMembers] = useState([]);
  const [supportMsg, setSupportMsg] = useState({});
  const [sending, setSending] = useState({});
  const [sent, setSent] = useState({});
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const loadMembers = useCallback(async () => {
    if (!group) return;
    try {
      const res = await groups.getMembers(group._id || group.id);
      setMembers(res.data);
    } catch (err) {
      console.error('Failed to load members:', err);
    }
  }, [group]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const getAvgEnergy = (checkin) => {
    if (!checkin) return null;
    const vals = ENERGY_TYPES.map((t) => checkin[t.key] ?? 0);
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  };

  const sendSupport = async (memberId) => {
    const message = supportMsg[memberId];
    if (!message?.trim()) return;
    setSending({ ...sending, [memberId]: true });
    try {
      await support.send({ recipientId: memberId, message, groupId: group._id || group.id });
      setSupportMsg({ ...supportMsg, [memberId]: '' });
      setSent({ ...sent, [memberId]: true });
      setTimeout(() => setSent((s) => ({ ...s, [memberId]: false })), 3000);
    } catch (err) {
      console.error('Failed to send support:', err);
    } finally {
      setSending((s) => ({ ...s, [memberId]: false }));
    }
  };

  if (!group) {
    return (
      <div className="card">
        <div className="card-header">
          <Users size={22} />
          <h2>Group Members</h2>
        </div>
        <p className="empty-state">Select a group to see members</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <Users size={22} />
        <h2>{group.name}</h2>
        <button className="btn btn-small btn-icon" onClick={loadMembers} title="Refresh">
          <RefreshCw size={16} />
        </button>
      </div>
      <div className="members-list">
        {members.length === 0 ? (
          <p className="empty-state">No members found</p>
        ) : (
          members.map((m) => {
            const memberId = m.userId?._id || m.userId?.id || m._id || m.id;
            const name = m.userId?.username || m.username || 'Unknown';
            const checkin = m.latestCheckin || m.checkin;
            const avg = getAvgEnergy(checkin);
            const isLow = avg !== null && avg <= 35;
            const isMe = memberId === (user._id || user.id);

            return (
              <div key={memberId} className={`member-card ${isLow ? 'low-energy' : ''}`}>
                <div className="member-header">
                  <span className="member-name">{name}{isMe ? ' (you)' : ''}</span>
                  {avg !== null ? (
                    <span className={`avg-badge ${isLow ? 'badge-low' : avg <= 50 ? 'badge-mid' : 'badge-high'}`}>
                      {avg}% avg
                    </span>
                  ) : (
                    <span className="avg-badge badge-none">No check-in</span>
                  )}
                </div>
                {checkin && (
                  <div className="member-gauges">
                    {ENERGY_TYPES.map((type) => (
                      <BatteryGauge
                        key={type.key}
                        value={checkin[type.key] ?? 0}
                        color={type.color}
                        label={type.label}
                        icon={type.icon}
                        readOnly
                      />
                    ))}
                  </div>
                )}
                {!isMe && (
                  <div className="support-row">
                    <input
                      type="text"
                      placeholder={isLow ? 'Send encouragement...' : 'Send a message...'}
                      value={supportMsg[memberId] || ''}
                      onChange={(e) => setSupportMsg({ ...supportMsg, [memberId]: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && sendSupport(memberId)}
                    />
                    <button
                      className="btn btn-small btn-accent"
                      onClick={() => sendSupport(memberId)}
                      disabled={sending[memberId] || !supportMsg[memberId]?.trim()}
                    >
                      {sent[memberId] ? <Check size={16} /> : <Send size={16} />}
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Admin Panel ───

function AdminPanel() {
  const [stats, setStats] = useState({ users: 0, groups: 0, checkins: 0 });
  const [users, setUsers] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [analytics, setAnalytics] = useState({ usersOverTime: [], checkinsPerDay: [] });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const loadAdminData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, groupsRes, analyticsRes] = await Promise.all([
        admin.getUsers(),
        admin.getGroups(),
        admin.getAnalytics(),
      ]);
      const usersData = usersRes.data?.users || usersRes.data || [];
      const groupsData = groupsRes.data?.groups || groupsRes.data || [];
      const analyticsData = analyticsRes.data || {};
      setUsers(usersData);
      setGroupList(groupsData);
      setAnalytics({
        usersOverTime: analyticsData.usersOverTime || [],
        checkinsPerDay: analyticsData.checkinsPerDay || [],
      });
      setStats({
        users: analyticsData.totalUsers ?? usersData.length,
        groups: analyticsData.totalGroups ?? groupsData.length,
        checkins: analyticsData.totalCheckins ?? 0,
      });
    } catch (err) {
      console.error('Failed to load admin data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const handleSuspend = async (userId, isSuspended) => {
    setActionLoading(userId);
    try {
      if (isSuspended) {
        await admin.unsuspendUser(userId);
      } else {
        await admin.suspendUser(userId);
      }
      setUsers((prev) =>
        prev.map((u) => (u.id === userId || u._id === userId) ? { ...u, suspended: !isSuspended } : u)
      );
    } catch (err) {
      console.error('Failed to update user status:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <RefreshCw size={24} className="spin" style={{ color: 'var(--primary)' }} />
          <p style={{ color: 'var(--text-muted)', marginTop: '12px' }}>Loading admin data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      {/* Stats Cards */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}>
            <Users size={24} />
          </div>
          <div className="admin-stat-info">
            <span className="admin-stat-number">{stats.users}</span>
            <span className="admin-stat-label">Total Users</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80' }}>
            <Hash size={24} />
          </div>
          <div className="admin-stat-info">
            <span className="admin-stat-number">{stats.groups}</span>
            <span className="admin-stat-label">Total Groups</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: 'rgba(244,114,182,0.15)', color: '#f472b6' }}>
            <Activity size={24} />
          </div>
          <div className="admin-stat-info">
            <span className="admin-stat-number">{stats.checkins}</span>
            <span className="admin-stat-label">Total Check-Ins</span>
          </div>
        </div>
      </div>

      {/* Analytics Charts */}
      {(analytics.usersOverTime.length > 0 || analytics.checkinsPerDay.length > 0) && (
        <div className="admin-charts-grid">
          {analytics.usersOverTime.length > 0 && (
            <div className="card">
              <div className="card-header">
                <TrendingUp size={20} />
                <h2>Users Over Time</h2>
              </div>
              <div className="chart-container" style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analytics.usersOverTime}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12} />
                    <YAxis stroke="var(--text-muted)" fontSize={12} />
                    <Tooltip
                      contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }}
                      labelStyle={{ color: 'var(--text)' }}
                    />
                    <Area type="monotone" dataKey="count" stroke="#6366f1" fill="rgba(99,102,241,0.2)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {analytics.checkinsPerDay.length > 0 && (
            <div className="card">
              <div className="card-header">
                <BarChart3 size={20} />
                <h2>Check-Ins Per Day</h2>
              </div>
              <div className="chart-container" style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.checkinsPerDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12} />
                    <YAxis stroke="var(--text-muted)" fontSize={12} />
                    <Tooltip
                      contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }}
                      labelStyle={{ color: 'var(--text)' }}
                    />
                    <Bar dataKey="count" fill="#f472b6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users Table */}
      <div className="card">
        <div className="card-header">
          <Users size={22} />
          <h2>Users</h2>
          <span className="admin-count-badge">{users.length}</span>
        </div>
        {users.length === 0 ? (
          <p className="empty-state">No users found</p>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const uid = u.id || u._id;
                  return (
                    <tr key={uid} className={u.suspended ? 'row-suspended' : ''}>
                      <td className="admin-user-cell">
                        <div className="admin-user-avatar">
                          {(u.username || u.name || '?')[0].toUpperCase()}
                        </div>
                        <span>{u.username || u.name}</span>
                      </td>
                      <td className="admin-email-cell">{u.email}</td>
                      <td className="admin-date-cell">
                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <span className={`admin-status-badge ${u.suspended ? 'status-suspended' : 'status-active'}`}>
                          {u.suspended ? 'Suspended' : 'Active'}
                        </span>
                      </td>
                      <td>
                        <button
                          className={`btn btn-small ${u.suspended ? 'btn-unsuspend' : 'btn-suspend'}`}
                          onClick={() => handleSuspend(uid, u.suspended)}
                          disabled={actionLoading === uid}
                        >
                          {actionLoading === uid ? (
                            <RefreshCw size={14} className="spin" />
                          ) : u.suspended ? (
                            <><UserCheck size={14} /> Unsuspend</>
                          ) : (
                            <><UserX size={14} /> Suspend</>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Groups Table */}
      <div className="card">
        <div className="card-header">
          <Hash size={22} />
          <h2>Groups</h2>
          <span className="admin-count-badge">{groupList.length}</span>
        </div>
        {groupList.length === 0 ? (
          <p className="empty-state">No groups found</p>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Members</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {groupList.map((g) => (
                  <tr key={g.id || g._id}>
                    <td style={{ fontWeight: 600 }}>{g.name}</td>
                    <td><code className="admin-code">{g.code}</code></td>
                    <td>{g.memberCount ?? g.members?.length ?? '—'}</td>
                    <td className="admin-date-cell">
                      {g.createdAt ? new Date(g.createdAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Account Deletion Modal ───

function DeleteAccountModal({ onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') return;
    setDeleting(true);
    setError('');
    try {
      await user.deleteAccount();
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-card">
        <div className="modal-header-danger">
          <AlertTriangle size={28} />
          <h2>Delete Your Account</h2>
        </div>
        <p className="modal-body-text">
          Are you sure? This will <strong>permanently delete all your data</strong>, including
          check-ins, group memberships, preferences, and support messages. This action cannot be undone.
        </p>
        <div className="modal-confirm-field">
          <label className="modal-confirm-label">Type <strong>DELETE</strong> to confirm</label>
          <input
            type="text"
            className="modal-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            autoFocus
          />
        </div>
        {error && <div className="error-msg">{error}</div>}
        <div className="modal-actions">
          <button className="btn btn-small" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={confirmText !== 'DELETE' || deleting}
          >
            {deleting ? <RefreshCw size={16} className="spin" /> : <Trash2 size={16} />}
            {deleting ? 'Deleting...' : 'Permanently Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Settings Tab ───

const FREQUENCY_OPTIONS = [
  { value: 1, label: 'Every 1 hour' },
  { value: 2, label: 'Every 2 hours' },
  { value: 3, label: 'Every 3 hours' },
  { value: 4, label: 'Every 4 hours' },
];

function SettingsTab({ schedulePrefs, onSavePrefs, onLogout }) {
  const [prefs, setPrefs] = useState(schedulePrefs);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  useEffect(() => {
    setPrefs(schedulePrefs);
  }, [schedulePrefs]);

  const handleSave = async (updates) => {
    const newPrefs = { ...prefs, ...updates };
    setPrefs(newPrefs);
    setSaving(true);
    try {
      if (newPrefs.checkinFrequencyHours > 0 && typeof Notification !== 'undefined' && Notification.permission === 'default') {
        const perm = await Notification.requestPermission();
        setNotifPermission(perm);
      }
      await onSavePrefs(newPrefs);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-layout">
      <div className="card">
        <div className="card-header">
          <Clock size={22} />
          <h2>Your Schedule</h2>
          {saved && (
            <span className="settings-saved">
              <Check size={14} /> Saved
            </span>
          )}
        </div>
        <p className="settings-description">
          Your check-in reminders are scheduled around your daily routine.
          Adjust your wake time, bedtime, and frequency below.
        </p>

        <ScheduleTimeline prefs={prefs} />

        <div className="settings-grid">
          <div className="settings-field">
            <label className="settings-label">
              <Sunrise size={16} /> Wake time
            </label>
            <input
              type="time"
              className="settings-time-input"
              value={prefs.wakeTime || '07:00'}
              onChange={(e) => handleSave({ wakeTime: e.target.value })}
            />
          </div>
          <div className="settings-field">
            <label className="settings-label">
              <MoonStar size={16} /> Bedtime
            </label>
            <input
              type="time"
              className="settings-time-input"
              value={prefs.bedtime || '22:00'}
              onChange={(e) => handleSave({ bedtime: e.target.value })}
            />
          </div>
          <div className="settings-field">
            <label className="settings-label">
              <Timer size={16} /> First check-in
            </label>
            <select
              className="settings-select"
              value={prefs.firstCheckinDelay || 0}
              onChange={(e) => handleSave({ firstCheckinDelay: Number(e.target.value) })}
            >
              {FIRST_CHECKIN_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="settings-field">
            <label className="settings-label">
              <Bell size={16} /> Frequency
            </label>
            <select
              className="settings-select"
              value={prefs.checkinFrequencyHours || 0}
              onChange={(e) => handleSave({ checkinFrequencyHours: Number(e.target.value) })}
              disabled={saving}
            >
              <option value={0}>Off — no reminders</option>
              {FREQUENCY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="settings-toggle-row">
          <div className="settings-toggle-info">
            <Moon size={16} />
            <div>
              <strong>Bedtime check-in</strong>
              <span className="setting-desc">Gentle reminder 30 min before bedtime</span>
            </div>
          </div>
          <button
            className={`settings-toggle ${prefs.bedtimeCheckinEnabled ? 'toggle-on' : ''}`}
            onClick={() => handleSave({ bedtimeCheckinEnabled: !prefs.bedtimeCheckinEnabled })}
          >
            <div className="toggle-thumb" />
          </button>
        </div>

        {notifPermission === 'denied' && prefs.checkinFrequencyHours > 0 && (
          <div className="settings-warning">
            Browser notifications are blocked. Please enable them in your browser settings to receive check-in reminders.
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <Zap size={22} />
          <h2>Push Notifications</h2>
        </div>
        <p className="settings-description">
          Receive push notifications for group alerts, support messages, and check-in reminders — even when the app is closed.
        </p>
        <NotificationSettings />
      </div>

      {/* Danger Zone */}
      <div className="card danger-zone-card">
        <div className="card-header">
          <AlertTriangle size={22} color="var(--danger)" />
          <h2 style={{ color: 'var(--danger)' }}>Danger Zone</h2>
        </div>
        <p className="settings-description">
          Permanently delete your account and all associated data. This action cannot be undone.
        </p>
        <button
          className="btn btn-danger"
          onClick={() => setShowDeleteModal(true)}
        >
          <Trash2 size={16} />
          Delete My Account
        </button>
      </div>

      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => {
            setShowDeleteModal(false);
            if (onLogout) onLogout();
          }}
        />
      )}
    </div>
  );
}

// ─── Alerts Panel ───

function AlertsPanel({ alertList, onMarkRead, onNavigate, onClose }) {
  if (!alertList || alertList.length === 0) {
    return (
      <div className="alerts-panel">
        <div className="alerts-panel-header">
          <AlertTriangle size={16} />
          <span>Alerts</span>
          <button className="btn btn-small btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="alerts-empty">No unread alerts</div>
      </div>
    );
  }

  return (
    <div className="alerts-panel">
      <div className="alerts-panel-header">
        <AlertTriangle size={16} color="#ef4444" />
        <span>Critical Alerts ({alertList.length})</span>
        <button className="btn btn-small btn-ghost" onClick={onClose}><X size={16} /></button>
      </div>
      <div className="alerts-list">
        {alertList.map((alert) => (
          <div key={alert.id} className="alert-item alert-critical">
            <div className="alert-item-content" onClick={() => onNavigate(alert)}>
              <div className="alert-item-badge">
                <AlertTriangle size={14} color="#ef4444" />
                <span className="alert-type-label">URGENT</span>
              </div>
              <p className="alert-message">{alert.message}</p>
              <div className="alert-meta">
                <span className="alert-group">{alert.group_name}</span>
                <span className="alert-time">{getTimeAgo(alert.created_at)}</span>
              </div>
            </div>
            <button
              className="btn btn-small btn-ghost alert-dismiss"
              onClick={(e) => { e.stopPropagation(); onMarkRead(alert.id); }}
              title="Mark as read"
            >
              <Check size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Reminder Banner ───

function useCheckinReminder(schedulePrefs, lastCheckinTime) {
  const [isDue, setIsDue] = useState(false);
  const [countdown, setCountdown] = useState('');
  const [reminderType, setReminderType] = useState('checkin');
  const notifiedRef = React.useRef(false);

  useEffect(() => {
    const freq = schedulePrefs?.checkinFrequencyHours;
    if (!freq || freq <= 0) {
      setIsDue(false);
      setCountdown('');
      return;
    }

    const tick = () => {
      const now = new Date();
      const nowMins = now.getHours() * 60 + now.getMinutes();
      const wakeMins = parseTime(schedulePrefs.wakeTime);
      const bedMins = parseTime(schedulePrefs.bedtime);
      const effectiveBed = bedMins <= wakeMins ? bedMins + 1440 : bedMins;
      const nowAdjusted = nowMins < wakeMins ? nowMins + 1440 : nowMins;

      // Outside waking hours — suppress
      if (nowAdjusted < wakeMins || nowAdjusted >= effectiveBed) {
        setIsDue(false);
        const wakeToday = new Date(now);
        const wakeH = Math.floor(wakeMins / 60);
        const wakeM = wakeMins % 60;
        wakeToday.setHours(wakeH, wakeM, 0, 0);
        if (wakeToday <= now) wakeToday.setDate(wakeToday.getDate() + 1);
        const remaining = wakeToday.getTime() - now.getTime();
        const hrs = Math.floor(remaining / 3600000);
        const mins = Math.floor((remaining % 3600000) / 60000);
        setCountdown(`${hrs}h ${mins}m (after wake)`);
        return;
      }

      // Check bedtime reminder (30 min before bed)
      if (schedulePrefs.bedtimeCheckinEnabled) {
        const bedtimeReminderMins = effectiveBed - 30;
        if (nowAdjusted >= bedtimeReminderMins && nowAdjusted < effectiveBed) {
          const lastTime = lastCheckinTime ? new Date(lastCheckinTime).getTime() : 0;
          const thirtyMinsAgo = now.getTime() - 30 * 60 * 1000;
          if (lastTime < thirtyMinsAgo) {
            setIsDue(true);
            setCountdown('Now');
            setReminderType('bedtime');
            if (!notifiedRef.current && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              notifiedRef.current = true;
              new Notification('Recovery Recharge', {
                body: 'Bedtime approaching — time for your evening check-in!',
                icon: '/favicon.ico',
                tag: 'checkin-reminder',
              });
            }
            return;
          }
        }
      }

      // Regular frequency-based check
      const intervalMs = freq * 60 * 60 * 1000;
      const lastTime = lastCheckinTime ? new Date(lastCheckinTime).getTime() : 0;
      const firstCheckinMins = wakeMins + (schedulePrefs.firstCheckinDelay || 0);

      let nextDue;
      if (lastTime > 0) {
        nextDue = lastTime + intervalMs;
        // Don't schedule before first checkin time
        const todayFirst = new Date(now);
        todayFirst.setHours(Math.floor(firstCheckinMins / 60), firstCheckinMins % 60, 0, 0);
        if (nextDue < todayFirst.getTime() && now.getTime() >= todayFirst.getTime()) {
          nextDue = todayFirst.getTime();
        }
      } else {
        const todayFirst = new Date(now);
        todayFirst.setHours(Math.floor(firstCheckinMins / 60), firstCheckinMins % 60, 0, 0);
        nextDue = todayFirst.getTime();
      }

      const remaining = nextDue - now.getTime();

      if (remaining <= 0) {
        setIsDue(true);
        setCountdown('Now');
        setReminderType('checkin');
        if (!notifiedRef.current && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          notifiedRef.current = true;
          new Notification('Recovery Recharge', {
            body: 'Time for your energy check-in!',
            icon: '/favicon.ico',
            tag: 'checkin-reminder',
          });
        }
      } else {
        setIsDue(false);
        notifiedRef.current = false;
        setReminderType('checkin');
        const hrs = Math.floor(remaining / 3600000);
        const mins = Math.floor((remaining % 3600000) / 60000);
        const secs = Math.floor((remaining % 60000) / 1000);
        if (hrs > 0) {
          setCountdown(`${hrs}h ${mins}m`);
        } else if (mins > 0) {
          setCountdown(`${mins}m ${secs}s`);
        } else {
          setCountdown(`${secs}s`);
        }
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [schedulePrefs, lastCheckinTime]);

  return { isDue, countdown, reminderType };
}

function ReminderBanner({ isDue, countdown, reminderType, checkinFrequencyHours, onGoToCheckin }) {
  if (!checkinFrequencyHours || checkinFrequencyHours <= 0) return null;

  const isBedtime = reminderType === 'bedtime';

  return (
    <div className={`reminder-banner ${isDue ? 'reminder-due' : ''} ${isBedtime && isDue ? 'reminder-bedtime' : ''}`}>
      <div className="reminder-left">
        {isDue ? (
          isBedtime ? <MoonStar size={18} className="reminder-icon-ring" /> : <BellRing size={18} className="reminder-icon-ring" />
        ) : <Timer size={18} />}
        <span className="reminder-text">
          {isDue
            ? isBedtime
              ? 'Bedtime approaching — time for your evening check-in'
              : 'Time for your energy check-in!'
            : `Next check-in in ${countdown}`
          }
        </span>
      </div>
      {isDue && (
        <button className="btn btn-small btn-accent" onClick={onGoToCheckin}>
          <Battery size={14} /> Check In Now
        </button>
      )}
    </div>
  );
}

// ─── PWA Install Prompt ───

function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('pwa-install-dismissed') === '1');

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installed = () => setDeferredPrompt(null);
    window.addEventListener('appinstalled', installed);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') setDeferredPrompt(null);
  };

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('pwa-install-dismissed', '1');
  };

  const showBanner = deferredPrompt && !dismissed;
  return { showBanner, install, dismiss };
}

function InstallBanner({ onInstall, onDismiss }) {
  return (
    <div className="install-banner">
      <div className="install-banner-content">
        <Download size={20} />
        <div className="install-banner-text">
          <strong>Install Recovery Recharge</strong>
          <span>Add to your home screen for quick access</span>
        </div>
      </div>
      <div className="install-banner-actions">
        <button className="btn btn-small btn-primary" onClick={onInstall}>Install</button>
        <button className="btn btn-small btn-ghost" onClick={onDismiss}><X size={16} /></button>
      </div>
    </div>
  );
}

// ─── Notification Settings ───

function NotificationSettings() {
  const [permission, setPermission] = useState(getNotificationPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isPushSupported()) {
      getExistingSubscription().then((sub) => {
        setSubscribed(!!sub);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const handleEnable = async () => {
    const perm = await requestNotificationPermission();
    setPermission(perm);
    if (perm === 'granted') {
      const sub = await subscribeToPush();
      setSubscribed(!!sub);
    }
  };

  const handleDisable = async () => {
    await unsubscribeFromPush();
    setSubscribed(false);
  };

  if (!isPushSupported()) {
    return (
      <div className="setting-item">
        <Bell size={18} />
        <div className="setting-info">
          <strong>Push Notifications</strong>
          <span className="setting-desc">Not supported on this browser</span>
        </div>
      </div>
    );
  }

  return (
    <div className="setting-item">
      <Bell size={18} />
      <div className="setting-info">
        <strong>Push Notifications</strong>
        <span className="setting-desc">
          {permission === 'denied'
            ? 'Blocked — enable in browser settings'
            : subscribed ? 'Receiving notifications' : 'Get reminders and group alerts'}
        </span>
      </div>
      {permission !== 'denied' && !loading && (
        <button
          className={`btn btn-small ${subscribed ? 'btn-ghost' : 'btn-primary'}`}
          onClick={subscribed ? handleDisable : handleEnable}
        >
          {subscribed ? 'Disable' : 'Enable'}
        </button>
      )}
    </div>
  );
}

// ─── Main App ───

function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState('landing'); // 'landing' | 'auth-login' | 'auth-signup' | 'app'
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [todayEntries, setTodayEntries] = useState([]);
  const [loadingToday, setLoadingToday] = useState(true);
  const [schedulePrefs, setSchedulePrefs] = useState({
    checkinFrequencyHours: 0,
    wakeTime: '07:00',
    bedtime: '22:00',
    firstCheckinDelay: 0,
    bedtimeCheckinEnabled: true,
    onboardingCompleted: false,
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [alertList, setAlertList] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [isNewSignup, setIsNewSignup] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (stored && token) {
      setUser(JSON.parse(stored));
      setPage('app');
    }
  }, []);

  const loadTodayEntries = useCallback(async () => {
    try {
      const res = await checkins.getToday();
      setTodayEntries(res.data || []);
    } catch (err) {
      console.error('Failed to load today check-ins:', err);
    } finally {
      setLoadingToday(false);
    }
  }, []);

  const loadAlerts = useCallback(async () => {
    try {
      const res = await alerts.getUnread();
      setAlertList(res.data || []);
    } catch (err) {
      console.error('Failed to load alerts:', err);
    }
  }, []);

  const loadPreferences = useCallback(async () => {
    try {
      const res = await preferences.get();
      const data = res.data;
      setSchedulePrefs({
        checkinFrequencyHours: data.checkinFrequencyHours || 0,
        wakeTime: data.wakeTime || '07:00',
        bedtime: data.bedtime || '22:00',
        firstCheckinDelay: data.firstCheckinDelay ?? 0,
        bedtimeCheckinEnabled: data.bedtimeCheckinEnabled !== false,
        onboardingCompleted: data.onboardingCompleted || false,
      });
      // Show onboarding if not completed and this is a new signup
      if (!data.onboardingCompleted && isNewSignup) {
        setShowOnboarding(true);
      }
    } catch (err) {
      const stored = localStorage.getItem('checkinFrequencyHours');
      if (stored) setSchedulePrefs(prev => ({ ...prev, checkinFrequencyHours: Number(stored) }));
      if (isNewSignup) setShowOnboarding(true);
    }
  }, [isNewSignup]);

  useEffect(() => {
    if (!user) return;
    loadTodayEntries();
    loadPreferences();
    loadAlerts();
    const alertInterval = setInterval(loadAlerts, 30000);
    return () => clearInterval(alertInterval);
  }, [user, loadTodayEntries, loadPreferences, loadAlerts]);

  const handleSavePrefs = async (newPrefs) => {
    setSchedulePrefs(newPrefs);
    localStorage.setItem('checkinFrequencyHours', String(newPrefs.checkinFrequencyHours));
    try {
      await preferences.update(newPrefs);
    } catch (err) {
      console.warn('Could not save preferences to server, saved locally');
    }
  };

  const handleOnboardingComplete = (onboardingPrefs) => {
    setSchedulePrefs(prev => ({ ...prev, ...onboardingPrefs, onboardingCompleted: true }));
    setShowOnboarding(false);
    setIsNewSignup(false);
  };

  const lastCheckinTime = useMemo(() => {
    if (todayEntries.length === 0) return null;
    const sorted = [...todayEntries].sort((a, b) =>
      new Date(b.createdAt || b.timestamp).getTime() - new Date(a.createdAt || a.timestamp).getTime()
    );
    return sorted[0].createdAt || sorted[0].timestamp;
  }, [todayEntries]);

  const { isDue, countdown, reminderType } = useCheckinReminder(schedulePrefs, lastCheckinTime);
  const { showBanner: showInstallBanner, install: installApp, dismiss: dismissInstall } = useInstallPrompt();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setSelectedGroup(null);
    setActiveTab('dashboard');
    setPage('landing');
    setShowOnboarding(false);
    setIsNewSignup(false);
  };

  const handleLogin = (u, wasSignup) => {
    setUser(u);
    if (wasSignup) {
      setIsNewSignup(true);
    }
    setPage('app');
  };

  const handleCheckinComplete = () => {
    loadTodayEntries();
    loadAlerts();
  };

  const handleMarkAlertRead = async (alertId) => {
    try {
      await alerts.markRead(alertId);
      setAlertList((prev) => prev.filter((a) => a.id !== alertId));
    } catch (err) {
      console.error('Failed to mark alert as read:', err);
    }
  };

  const handleAlertNavigate = (alert) => {
    setSelectedGroup({ id: alert.group_id, name: alert.group_name });
    setActiveTab('groups');
    setShowAlerts(false);
  };

  if (page === 'landing' && !user) {
    return (
      <LandingPage
        onGetStarted={() => setPage('auth-signup')}
        onSignIn={() => setPage('auth-login')}
      />
    );
  }

  if ((page === 'auth-login' || page === 'auth-signup') && !user) {
    return (
      <AuthForm
        onLogin={handleLogin}
        initialSignup={page === 'auth-signup'}
        onBack={() => setPage('landing')}
      />
    );
  }

  return (
    <div className="app">
      {showOnboarding && (
        <OnboardingFlow onComplete={handleOnboardingComplete} />
      )}

      <header className="app-header">
        <div className="header-brand">
          <BatteryCharging size={28} className="logo-icon" />
          <h1>Recovery Recharge</h1>
        </div>
        <div className="header-user">
          <div className="alerts-trigger-wrapper">
            <button
              className={`btn btn-small btn-ghost alerts-trigger ${alertList.length > 0 ? 'has-alerts' : ''}`}
              onClick={() => setShowAlerts(!showAlerts)}
              title={`${alertList.length} unread alert${alertList.length !== 1 ? 's' : ''}`}
            >
              {alertList.length > 0 ? <BellRing size={18} color="#ef4444" /> : <Bell size={18} />}
              {alertList.length > 0 && <span className="alert-badge">{alertList.length}</span>}
            </button>
            {showAlerts && (
              <AlertsPanel
                alertList={alertList}
                onMarkRead={handleMarkAlertRead}
                onNavigate={handleAlertNavigate}
                onClose={() => setShowAlerts(false)}
              />
            )}
          </div>
          <span className="username">{user.username || user.name}</span>
          <button className="btn btn-small btn-ghost" onClick={handleLogout}>
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {showInstallBanner && (
        <InstallBanner onInstall={installApp} onDismiss={dismissInstall} />
      )}

      <nav className="tab-nav">
        {TAB_CONFIG.filter((tab) => !tab.adminOnly || user?.is_admin).map((tab) => (
          <button
            key={tab.key}
            className={`tab-btn ${activeTab === tab.key ? 'tab-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <tab.icon size={18} />
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>

      <ReminderBanner
        isDue={isDue}
        countdown={countdown}
        reminderType={reminderType}
        checkinFrequencyHours={schedulePrefs.checkinFrequencyHours}
        onGoToCheckin={() => setActiveTab('checkin')}
      />

      <main className="app-content">
        {activeTab === 'dashboard' && (
          <DashboardTab entries={todayEntries} loading={loadingToday} />
        )}

        {activeTab === 'checkin' && (
          <div className="checkin-layout">
            <CheckInPanel onCheckinComplete={handleCheckinComplete} />
            <CheckInTimeline entries={todayEntries} />
          </div>
        )}

        {activeTab === 'groups' && (
          <div className="groups-layout">
            <GroupPanel currentUser={user} onGroupChange={setSelectedGroup} />
            <MembersPanel group={selectedGroup} />
          </div>
        )}

        {activeTab === 'reports' && (
          <ReportsTab
            todayEntries={todayEntries}
            weekEntries={todayEntries}
            loadingWeek={false}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            schedulePrefs={schedulePrefs}
            onSavePrefs={handleSavePrefs}
            onLogout={handleLogout}
          />
        )}

        {activeTab === 'admin' && user?.is_admin && (
          <AdminPanel />
        )}
      </main>
    </div>
  );
}

export default App;
